import { useEffect } from 'react';

// App View: drag a bottom sheet or dialog down to close it, with a spring-back when let go early.
// One document-level handler, so no dialog needs editing:
//   [data-sheet]  the More menu / group picker panel (drag from anywhere while it is scrolled to the top)
//   .modal        every dialog (drag from its header / grab handle only, so forms and scrolling are safe)
// Closing clicks the sheet's own close button ([data-sheet-close] / .modal-close), or the overlay,
// so each page's normal close logic runs.
const FAR = 110;       // px dragged to close
const FLICK = 0.6;     // px/ms

export default function useSwipeToClose() {
  useEffect(() => {
    let st = null;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    const find = (t) => {
      const sheet = t.closest?.('[data-sheet]');
      if (sheet) return sheet.scrollTop <= 0 && !t.closest('input,textarea,select') ? sheet : null;
      const modal = t.closest?.('.modal');
      if (modal && t.closest('.modal-header') && !t.closest('button,input,select,textarea,a')) return modal;
      return null;
    };
    const closeFn = (el) => {
      const btn = el.querySelector('[data-sheet-close], .modal-close');
      if (btn) return () => btn.click();
      const overlay = el.closest('.modal-overlay');
      return overlay ? () => overlay.click() : null;
    };

    const start = (e) => {
      if (e.touches.length !== 1) return;
      const el = find(e.target);
      if (!el) return;
      st = { el, y0: e.touches[0].clientY, t0: Date.now(), dy: 0, on: false, close: closeFn(el) };
    };
    const move = (e) => {
      if (!st) return;
      const dy = e.touches[0].clientY - st.y0;
      if (!st.on) {
        if (dy < 8) { if (dy < -8) st = null; return; } // upward = normal scroll
        st.on = true;
      }
      st.dy = Math.max(0, dy);
      if (e.cancelable) e.preventDefault();
      st.el.style.transition = 'none';
      st.el.style.transform = `translateY(${st.dy}px)`;
    };
    const end = () => {
      if (!st) return;
      const { el, dy, on, t0, close } = st; st = null;
      if (!on) return;
      const fast = dy / Math.max(1, Date.now() - t0) > FLICK;
      if ((dy > FAR || fast) && close) {
        el.style.transition = reduce ? 'none' : 'transform .22s ease-in';
        el.style.transform = 'translateY(100%)';
        setTimeout(() => {
          close();
          // If the page kept it open, bring it back.
          setTimeout(() => { if (el.isConnected) { el.style.transition = ''; el.style.transform = ''; } }, 250);
        }, reduce ? 0 : 200);
      } else {
        el.style.transition = reduce ? 'none' : 'transform .4s cubic-bezier(.34,1.5,.5,1)';
        el.style.transform = '';
        setTimeout(() => { el.style.transition = ''; }, 420);
      }
    };

    document.addEventListener('touchstart', start, { passive: true });
    document.addEventListener('touchmove', move, { passive: false });
    document.addEventListener('touchend', end, { passive: true });
    document.addEventListener('touchcancel', end, { passive: true });
    return () => {
      document.removeEventListener('touchstart', start);
      document.removeEventListener('touchmove', move);
      document.removeEventListener('touchend', end);
      document.removeEventListener('touchcancel', end);
    };
  }, []);
}
